import React, { useMemo, useState } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { useNavigate } from "react-router-dom";
import {
  extenderPopColumns as baseColumns,
  ExtenderPop,
  shapeExtenderPopData,
} from "./ExtenderPopData";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { ListPageSkeleton, RefreshingIndicator } from "@shared/components/ListLoading";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { fetchExtenderPopList } from "./requests/request";
import { makeExtenderPopFields } from "./extenderPopFieldSpec";

const NO_EXTENDER_POPS: ExtenderPop[] = [];

const MainExtenderPop: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();

  // Cached per tenant: coming back to the list paints the last answer at once and
  // refreshes it underneath.
  const {
    data: extenderPops = NO_EXTENDER_POPS,
    error: fetchError,
    isLoading: isLoadingList,
    isRefreshing: isRefreshingList,
  } = useCachedFetch(tenantId ? `kontroledge/extender-pops?tenantId=${tenantId}` : null, async () => {
    const { status, data } = await fetchExtenderPopList(api, tenantId);
    if (status >= 500 || data.success === false) {
      throw new Error(data.message || "Unknown server error");
    }
    return Array.isArray(data.data) ? shapeExtenderPopData(data.data) : [];
  });
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  const fields = useMemo(
    () => makeExtenderPopFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const suggestedKeys = useMemo(() => {
    const headers = baseColumns.map((c) => c.header).filter(Boolean) as string[];
    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [isViewingAllCustomers]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<ExtenderPop>>();
    for (const f of fields) {
      m.set((f.key ?? labelId(f.label)).toLowerCase(), f);
    }
    return m;
  }, [fields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of fields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, id);
      m.set(f.label.toLowerCase(), id);
    }
    return m;
  }, [fields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else {
          token.op = ":";
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<ExtenderPop>(normalizedFilters, fields),
    [normalizedFilters, fields]
  );

  const filteredData = useMemo(
    () => extenderPops.filter(predicate),
    [extenderPops, predicate]
  );

  const columns = useMemo<GenericTableColumn<ExtenderPop>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: ExtenderPop) => row.customer?.name ?? "",
        renderCell: (row: ExtenderPop) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }

    return cols.map((col) => {
      if (col.header === "Extended PoP ID") {
        // Same link treatment MainPort gives the Port ID: navigate to the
        // dedicated detail view.
        return {
          ...col,
          renderCell: (row: ExtenderPop) => (
            <button
              {...viewHref(buildDetailPath("extenderPop", { extenderPopId: row.id }))}
              onClick={() =>
                navigate(buildDetailPath("extenderPop", { extenderPopId: row.id }))
              }
              className="
						hover:text-semantic-text-brand
						hover:underline
						focus:underline
						active:underline
						font-medium
						underline
						text-semantic-text-brand
						"
            >
              {row.serviceId}
            </button>
          ),
        };
      }
      return col;
    });
  }, [isViewingAllCustomers, navigate]);

  // Nothing to show yet: the page's shape in skeleton, not a screen-wide overlay.
  if (isLoadingList) {
    return <ListPageSkeleton />;
  }
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  if (extenderPops.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      <RefreshingIndicator active={isRefreshingList} />
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-3 my-5">
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <GenericTable
                  columns={columns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight="100%"
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MainExtenderPop;
